/*
This file is located in the main branch
Last time the file was updated (UTC): [26.08.2024 13:23:14]
Permissions to update outside the main branch: [true]
*/

import { Button, DropDown, Form, Modal } from '@finbeat/react-toolkit';
import useAuth from 'hooks/useAuth';
import { OutletAdd, OutletChange } from 'models/api/outlet';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useOutletWorkplaceState } from '../Outlets';
import OutletChangeModalBody from './OutletChangeModalBody';
import './OutletsChangeModal.scss';

export type OutletFormType = OutletAdd | OutletChange;

type OutletsChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    merchantRegions: DropDown[];
    typeWorks: DropDown[];
    isOutletDictsFetching: boolean;
    brandCode: DropDown[];
    isFetchingBrandCodeTypes: boolean;
};

export const defaultValues: OutletFormType = {
    tariffs: [{ tariffGroupId: null, initialFeeId: null }],
    isActive: false,
    tradeOrgId: undefined,
    questionaryId: undefined,
    settings: { isUseDefault: true, list: [{ id: null, type: '', value: '' }] },
    region: '',
    address: '',
    apiSettings: {
        id: null,
        url: null,
        login: null,
        password: null,
        useOrderActualization: false,
        httpHeaders: [{ header: '', value: '' }],
        useHttpHeaders: false,
        useProxy: false,
        proxySettings: {
            ip: null,
            port: null,
            authenticationMethod: null,
            login: null,
            password: null,
        },
    },
    contactFaces: [],
    pinfl: '',
    inn: '',
    brandCode: '',
    integrationType: 'TO',
    typeWork: 'B',
};

const OutletsChangeModal = ({
    isOpen = false,
    toggle,
    merchantRegions,
    typeWorks,
    isOutletDictsFetching,
    brandCode,
    isFetchingBrandCodeTypes,
}: OutletsChangeModalProps) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const { changeItemMutation, addItemMutation, currentItem } = useOutletWorkplaceState();

    const saveChanges = async (outlet: OutletFormType) => {
        if (outlet.settings.list) {
            outlet.settings.list.forEach((i) => (i.value = i.value.toString()));
        }

        if ('id' in outlet) {
            changeItemMutation.mutate(outlet);
        } else {
            addItemMutation.mutate(outlet);
        }

        toggle(false);
    };

    const formHasRole = useMemo(
        () => (currentItem ? hasRole('outletsUpdate') : hasRole('outletsCreate')),
        [hasRole]
    );

    const showAdditionalSettings = useMemo(
        () => hasRole('outletsChangeAddtionalFields'),
        [hasRole]
    );

    const showSettings = useMemo(() => hasRole('outletsChangeSettings'), [hasRole]);

    return (
        <Modal size="lg" isOpen={isOpen} toggle={() => toggle(false)} scrollable>
            <Form<OutletFormType>
                onSubmit={saveChanges}
                defaultValues={currentItem || defaultValues}
                className="form-in-modal"
            >
                <ModalHeader toggle={() => toggle(false)}>
                    {currentItem ? `#${currentItem.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="custom-scrollbar outlets__body">
                    <OutletChangeModalBody
                        formHasRole={formHasRole}
                        showAdditionalSettings={showAdditionalSettings}
                        showSettings={showSettings}
                        merchantRegions={merchantRegions}
                        typeWorks={typeWorks}
                        isOutletDictsFetching={isOutletDictsFetching}
                        brandCode={brandCode}
                        isFetchingBrandCodeTypes={isFetchingBrandCodeTypes}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggle(false)}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" type="submit" disabled={!formHasRole}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default OutletsChangeModal;
